/**
 * Find a Store — list/map locator with Shopify location selection + optional StoreRocket map.
 */
class FindAStore extends HTMLElement {
  connectedCallback() {
    this._view = 'list';
    this._filters = new Set();
    this._query = '';
    this._coords = null;
    this._stores = [];
    this._selectedId = this._readLocationId();
    this._mapLoaded = false;

    const sourceId = this.dataset.storesSource;
    const sourceEl = sourceId ? document.getElementById(sourceId) : null;
    if (sourceEl) {
      try {
        this._stores = JSON.parse(sourceEl.textContent || '[]');
      } catch (error) {
        this._stores = [];
      }
    }

    this._listEl = this.querySelector('[data-fas-list]');
    this._mapEl = this.querySelector('[data-fas-map]');
    this._emptyEl = this.querySelector('[data-fas-empty]');
    this._countEl = this.querySelector('[data-fas-filter-count]');
    this._searchInput = this.querySelector('[data-fas-search]');
    this._filterPanel = this.querySelector('[data-fas-filter-panel]');
    this._chipsEl = this.querySelector('[data-fas-chips]');
    this._mapMountPending = false;

    this._bindEvents();
    this._renderCards();
    this._updateFilterUi();
    this._setView(this.dataset.defaultView === 'map' ? 'map' : 'list');

    document.addEventListener('age-gate:store-selected', (event) => {
      this._selectedId = String(event.detail?.storeId || this._readLocationId() || '');
      this._renderCards();
    });

    window.addEventListener('storage', (event) => {
      if (event.key === '_selected_location_id') {
        this._selectedId = this._readLocationId();
        this._renderCards();
      }
    });
  }

  _bindEvents() {
    this.querySelectorAll('[data-fas-view]').forEach((button) => {
      button.addEventListener('click', () => this._setView(button.dataset.fasView));
    });

    this.querySelectorAll('[data-fas-filter]').forEach((button) => {
      button.addEventListener('click', () => {
        const key = button.dataset.fasFilter;
        if (!key) return;
        if (this._filters.has(key)) {
          this._filters.delete(key);
        } else {
          this._filters.add(key);
        }
        this._updateFilterUi();
        this._renderCards();
      });
    });

    const filtersToggle = this.querySelector('[data-fas-filters-toggle]');
    if (filtersToggle && this._filterPanel) {
      filtersToggle.addEventListener('click', (event) => {
        event.stopPropagation();
        this._toggleFilterPanel();
      });

      document.addEventListener('click', (event) => {
        if (this._filterPanel.hasAttribute('hidden')) return;
        if (event.target.closest('[data-fas-filter-panel]') || event.target.closest('[data-fas-filters-toggle]')) {
          return;
        }
        this._closeFilterPanel();
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this._closeFilterPanel();
      });
    }

    if (this._searchInput) {
      this._searchInput.addEventListener('input', () => {
        this._query = this._searchInput.value.trim().toLowerCase();
        this._renderCards();
      });
    }

    const locateBtn = this.querySelector('[data-fas-locate]');
    if (locateBtn) {
      locateBtn.addEventListener('click', () => this._useMyLocation(locateBtn));
    }

    this.addEventListener('click', (event) => {
      const selectBtn = event.target.closest('[data-fas-select]');
      if (selectBtn) {
        event.preventDefault();
        this._selectStore(selectBtn.dataset.fasSelect);
        return;
      }

      const closeChip = event.target.closest('[data-fas-chip-remove]');
      if (closeChip) {
        event.preventDefault();
        this._filters.delete(closeChip.dataset.fasChipRemove);
        this._updateFilterUi();
        this._renderCards();
      }
    });
  }

  _readLocationId() {
    const raw = window.localStorage.getItem('_selected_location_id');
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw);
      return parsed == null ? null : String(parsed);
    } catch (error) {
      return String(raw);
    }
  }

  _setView(view) {
    this._view = view === 'map' ? 'map' : 'list';
    this.dataset.view = this._view;

    this.querySelectorAll('[data-fas-view]').forEach((button) => {
      const active = button.dataset.fasView === this._view;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (this._listEl) this._listEl.hidden = this._view !== 'list';
    if (this._mapEl) this._mapEl.hidden = this._view !== 'map';

    if (this._view === 'map') {
      this._ensureMap();
    }
  }

  _toggleFilterPanel() {
    if (!this._filterPanel) return;
    if (this._filterPanel.hasAttribute('hidden')) {
      this._openFilterPanel();
    } else {
      this._closeFilterPanel();
    }
  }

  _openFilterPanel() {
    if (!this._filterPanel) return;
    this._filterPanel.removeAttribute('hidden');
    const filtersToggle = this.querySelector('[data-fas-filters-toggle]');
    if (filtersToggle) filtersToggle.setAttribute('aria-expanded', 'true');
  }

  _closeFilterPanel() {
    if (!this._filterPanel || this._filterPanel.hasAttribute('hidden')) return;
    this._filterPanel.setAttribute('hidden', '');
    const filtersToggle = this.querySelector('[data-fas-filters-toggle]');
    if (filtersToggle) filtersToggle.setAttribute('aria-expanded', 'false');
  }

  _ensureMap() {
    if (this._mapLoaded) return;
    this._mapLoaded = true;

    const embedHost = this.querySelector('[data-fas-storerocket]');
    if (!embedHost) return;

    if (embedHost.dataset.embedTemplateId) {
      this._mountEmbedTemplate(embedHost);
      return;
    }

    if (embedHost.dataset.embedReady === 'true') {
      this._refreshMapLayout();
      return;
    }

    const account = (this.dataset.storerocketAccount || '').trim();
    if (!account) return;

    embedHost.innerHTML = '';
    const mount = document.createElement('div');
    mount.id = `storerocket-widget-${this.dataset.sectionId || 'fas'}`;
    embedHost.appendChild(mount);

    const existing = document.querySelector('script[data-fas-storerocket-loader]');
    const boot = () => {
      if (window.StoreRocket && typeof window.StoreRocket.init === 'function') {
        try {
          window.StoreRocket.init();
        } catch (error) {
          /* widget self-inits on some accounts */
        }
      }
    };

    if (existing) {
      boot();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://cdn.storerocket.io/widget.js';
    script.async = true;
    script.dataset.fasStorerocketLoader = 'true';
    script.dataset.storerocketId = account;
    script.setAttribute('data-storerocket-id', account);
    script.onload = boot;
    document.body.appendChild(script);

    this._refreshMapLayout();
  }

  _initStoreRocket() {
    /* deprecated — handled in _ensureMap */
  }

  _mountEmbedTemplate(embedHost) {
    const templateId = embedHost.dataset.embedTemplateId;
    if (!templateId || this._mapMountPending) return;

    const template = document.getElementById(templateId);
    if (!template) return;

    this._mapMountPending = true;
    embedHost.innerHTML = 'value' in template ? template.value : template.innerHTML;
    this._executeEmbeddedScripts(embedHost);
    embedHost.dataset.embedReady = 'true';
    delete embedHost.dataset.embedTemplateId;
    this._mapMountPending = false;
    this._refreshMapLayout();
  }

  _executeEmbeddedScripts(root) {
    root.querySelectorAll('script').forEach((oldScript) => {
      const newScript = document.createElement('script');

      Array.from(oldScript.attributes).forEach((attribute) => {
        newScript.setAttribute(attribute.name, attribute.value);
      });

      if (oldScript.textContent) {
        newScript.textContent = oldScript.textContent;
      }

      oldScript.replaceWith(newScript);
    });
  }

  _refreshMapLayout() {
    window.requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));

      window.setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 300);
    });
  }

  _useMyLocation(button) {
    if (!navigator.geolocation) return;

    const label = button.querySelector('[data-fas-locate-label]');
    const original = label ? label.textContent : '';
    button.disabled = true;
    if (label) label.textContent = this.dataset.locatingLabel || 'Locating…';

    navigator.geolocation.getCurrentPosition(
      (position) => {
        this._coords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        };
        button.disabled = false;
        if (label) label.textContent = original;
        this._renderCards();
      },
      () => {
        button.disabled = false;
        if (label) label.textContent = original;
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  }

  _haversineKm(lat1, lng1, lat2, lng2) {
    const toRad = (deg) => (deg * Math.PI) / 180;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

  _withDistance(store) {
    if (!this._coords || store.latitude == null || store.longitude == null) {
      return { ...store, distanceKm: null };
    }
    const distanceKm = this._haversineKm(
      this._coords.lat,
      this._coords.lng,
      Number(store.latitude),
      Number(store.longitude)
    );
    return { ...store, distanceKm };
  }

  _matchesFilters(store) {
    for (const key of this._filters) {
      if (key === 'pickup' && !store.pickup) return false;
      if (key === 'delivery' && !store.delivery) return false;
      if (key === 'bopis' && !store.pickup) return false;
      if (key === 'age_verification' && !store.ageVerification) return false;
      if (key === 'accessible' && !store.accessible) return false;
      if (key === 'parking' && !store.parking) return false;
    }
    return true;
  }

  _matchesQuery(store) {
    if (!this._query) return true;
    const haystack = [
      store.name,
      store.address,
      store.address2,
      store.city,
      store.province,
      store.provinceCode,
      store.zip,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return haystack.includes(this._query);
  }

  _filteredStores() {
    return this._stores
      .map((store) => this._withDistance(store))
      .filter((store) => this._matchesFilters(store) && this._matchesQuery(store))
      .sort((a, b) => {
        if (a.distanceKm == null && b.distanceKm == null) return 0;
        if (a.distanceKm == null) return 1;
        if (b.distanceKm == null) return -1;
        return a.distanceKm - b.distanceKm;
      });
  }

  _updateFilterUi() {
    this.querySelectorAll('[data-fas-filter]').forEach((button) => {
      const active = this._filters.has(button.dataset.fasFilter);
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });

    if (this._countEl) {
      const count = this._filters.size;
      this._countEl.hidden = count === 0;
      this._countEl.textContent = String(count);
    }

    const filtersToggle = this.querySelector('[data-fas-filters-toggle]');
    if (filtersToggle) {
      filtersToggle.classList.toggle('is-active', this._filters.size > 0);
    }

    if (!this._chipsEl) return;

    const labels = {
      pickup: this.dataset.labelPickup || 'Pickup',
      delivery: this.dataset.labelDelivery || 'Delivery',
      bopis: this.dataset.labelBopis || 'BOPIS',
      age_verification: this.dataset.labelAgeVerification || 'Age Verification',
      accessible: this.dataset.labelAccessible || 'Accessible',
      parking: this.dataset.labelParking || 'Parking',
    };

    this._chipsEl.innerHTML = '';
    this._filters.forEach((key) => {
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'find-a-store__chip is-active';
      chip.dataset.fasChipRemove = key;
      chip.innerHTML = `<span>${labels[key] || key}</span><span class="find-a-store__chip-x" aria-hidden="true"></span>`;
      this._chipsEl.appendChild(chip);
    });
  }

  _formatDistance(store) {
    if (store.distanceKm == null || Number.isNaN(store.distanceKm)) return '';
    const value = store.distanceKm < 10 ? store.distanceKm.toFixed(1) : Math.round(store.distanceKm);
    return `${value} km`;
  }

  _withLocationIdParam(url, locationId) {
    if (!url || locationId == null || locationId === '') return url;
    try {
      const parsed = new URL(url, window.location.origin);
      parsed.searchParams.set('location_id', String(locationId));
      return parsed.toString();
    } catch (error) {
      const joiner = url.includes('?') ? '&' : '?';
      return `${url}${joiner}location_id=${encodeURIComponent(String(locationId))}`;
    }
  }

  _formatAddress(store) {
    const line1 = [store.address, store.address2].filter(Boolean).join(', ');
    const line2 = [store.city, store.provinceCode || store.province, store.zip]
      .filter(Boolean)
      .join(', ');
    return [line1, line2].filter(Boolean).join('\n');
  }

  _isOpen(store) {
    if (typeof store.available === 'boolean') return store.available;
    return true;
  }

  _asset(name) {
    return this.dataset[`icon${name}`] || '';
  }

  _renderCards() {
    if (!this._listEl) return;

    const stores = this._filteredStores();
    const template =
      this.querySelector(`#FindAStore-${this.dataset.sectionId}-card-template`) ||
      this.querySelector('template');
    this._listEl.innerHTML = '';

    if (this._emptyEl) {
      this._emptyEl.hidden = stores.length > 0;
    }

    if (!template) return;

    stores.forEach((store) => {
      const node = template.content.cloneNode(true);
      const card = node.querySelector('.find-a-store__card');
      if (!card) return;

      const isCurrent = this._selectedId && String(store.id) === String(this._selectedId);
      card.classList.toggle('is-current', Boolean(isCurrent));

      const img = card.querySelector('[data-fas-card-image]');
      if (img) {
        const imageSrc = store.image || this.dataset.placeholderImage || '';
        if (imageSrc) {
          img.src = imageSrc;
          img.alt = store.name || '';
          img.hidden = false;
        } else {
          img.removeAttribute('src');
          img.hidden = true;
        }
      }

      const nameEl = card.querySelector('[data-fas-card-name]');
      if (nameEl) nameEl.textContent = store.name || '';

      // Each store has its own page now; the name is the link to it. Without a url the
      // element stays inert rather than rendering a dead anchor.
      const linkEl = card.querySelector('[data-fas-card-link]');
      if (linkEl) {
        if (store.url) {
          linkEl.setAttribute('href', store.url);
        } else {
          linkEl.removeAttribute('href');
        }
      }

      const addressEl = card.querySelector('[data-fas-card-address]');
      if (addressEl) addressEl.textContent = this._formatAddress(store);

      const distanceEl = card.querySelector('[data-fas-card-distance]');
      if (distanceEl) {
        const distance = this._formatDistance(store);
        const distanceValueEl = card.querySelector('[data-fas-card-distance-value]');
        if (distanceValueEl) distanceValueEl.textContent = distance;
        distanceEl.hidden = !distance;
      }

      const pickupEl = card.querySelector('[data-fas-card-pickup]');
      if (pickupEl) pickupEl.hidden = !store.pickup;

      const deliveryEl = card.querySelector('[data-fas-card-delivery]');
      if (deliveryEl) deliveryEl.hidden = !store.delivery;

      const statusEl = card.querySelector('[data-fas-card-status]');
      if (statusEl) {
        const open = this._isOpen(store);
        statusEl.textContent = open
          ? this.dataset.labelOpen || 'Open'
          : this.dataset.labelClosed || 'Closed';
        statusEl.classList.toggle('is-closed', !open);
      }

      const hoursEl = card.querySelector('[data-fas-card-hours]');
      if (hoursEl) {
        hoursEl.textContent = store.storeUntil || store.storeHours || '';
        hoursEl.closest('[data-fas-card-hours-wrap]')?.toggleAttribute('hidden', !hoursEl.textContent);
      }

      const phoneEl = card.querySelector('[data-fas-card-phone]');
      const phoneLink = card.querySelector('[data-fas-card-phone-link]');
      if (phoneEl) {
        phoneEl.textContent = store.phone || '';
        if (phoneLink && store.phone) {
          phoneLink.href = `tel:${String(store.phone).replace(/[^\d+]/g, '')}`;
        }
        phoneLink?.closest('[data-fas-card-phone-wrap]')?.toggleAttribute('hidden', !store.phone);
      }

      const badgeVerify = card.querySelector('[data-fas-badge-verify]');
      if (badgeVerify) badgeVerify.hidden = !store.ageVerification;

      const badgeAccessible = card.querySelector('[data-fas-badge-accessible]');
      if (badgeAccessible) badgeAccessible.hidden = !store.accessible;

      const badgeParking = card.querySelector('[data-fas-badge-parking]');
      if (badgeParking) badgeParking.hidden = !store.parking;

      const selectBtn = card.querySelector('[data-fas-select]');
      const currentEl = card.querySelector('[data-fas-current]');
      if (selectBtn) {
        selectBtn.dataset.fasSelect = store.id;
        selectBtn.hidden = Boolean(isCurrent);
      }
      if (currentEl) currentEl.hidden = !isCurrent;

      const infoLink = card.querySelector('[data-fas-info]');
      if (infoLink) {
        const globalInfoUrl = (this.dataset.infoUrl || '').trim();
        // The store's own page first. This used to build the configured info URL with
        // ?location_id=, which now only redirects here anyway — so every visitor paid a
        // round trip to reach the address the link could have pointed at directly, and
        // search engines were handed the redirecting form as the canonical one.
        if (store.url) {
          infoLink.href = store.url;
          infoLink.hidden = false;
        } else if (globalInfoUrl) {
          infoLink.href = this._withLocationIdParam(globalInfoUrl, store.id);
          infoLink.hidden = false;
        } else if (store.infoUrl) {
          infoLink.href = store.infoUrl;
          infoLink.hidden = false;
        } else if (store.latitude != null && store.longitude != null) {
          infoLink.href = `https://www.google.com/maps/dir/?api=1&destination=${store.latitude},${store.longitude}`;
          infoLink.hidden = false;
        } else {
          infoLink.hidden = true;
        }
      }

      this._listEl.appendChild(node);
    });
  }

  _selectStore(storeId) {
    if (!storeId) return;

    const locationId = String(storeId);
    const locationGid = `gid://shopify/Location/${locationId}`;
    let province = null;
    try {
      const rawProvince = window.localStorage.getItem('_selected_province');
      province = rawProvince ? JSON.parse(rawProvince) : rawProvince;
    } catch (error) {
      province = null;
    }

    const card = this._listEl && this._listEl.querySelector(`[data-fas-select="${storeId}"]`);
    const provinceFromCard = card && card.closest('[data-province]')
      ? card.closest('[data-province]').dataset.province
      : null;
    if (provinceFromCard) province = provinceFromCard;

    if (province && window.ValueBudsLocation && typeof window.ValueBudsLocation.toProvinceCode === 'function') {
      province = window.ValueBudsLocation.toProvinceCode(province);
    }

    this._selectedId = locationId;
    document.dispatchEvent(
      new CustomEvent('age-gate:store-selected', {
        detail: { storeId: locationId },
      })
    );

    const remapFormId = `location-cart-update-${storeId}`;
    const run = () => {
      if (window.ValueBudsLocation && typeof window.ValueBudsLocation.onLocationChange === 'function') {
        return window.ValueBudsLocation.onLocationChange({
          locationGid,
          locationId,
          province: province || '',
          remapFormId: document.getElementById(remapFormId) ? remapFormId : null,
        }).then((result) => {
          if (result && result.cancelled) {
            this._renderCards();
          }
        });
      }

      const form = document.getElementById(remapFormId);
      if (form) {
        let gidInput = form.querySelector('input[name="attributes[_selected_location]"]');
        if (!gidInput) {
          gidInput = document.createElement('input');
          gidInput.type = 'hidden';
          gidInput.name = 'attributes[_selected_location]';
          form.appendChild(gidInput);
        }
        gidInput.value = locationGid;

        if (province) {
          let provinceInput = form.querySelector('input[name="attributes[_selected_province]"]');
          if (!provinceInput) {
            provinceInput = document.createElement('input');
            provinceInput.type = 'hidden';
            provinceInput.name = 'attributes[_selected_province]';
            form.appendChild(provinceInput);
          }
          provinceInput.value = province;
        }

        form.submit();
        return Promise.resolve();
      }

      const root =
        (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const attributes = {
        _selected_location_id: locationId,
        _selected_location: locationGid,
      };
      if (province) attributes._selected_province = province;

      return fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes }),
      }).then(() => {
        window.location.reload();
      });
    };

    try {
      window.localStorage.setItem('_selected_location_id', JSON.stringify(locationId));
    } catch (error) {
      /* ignore */
    }

    run().catch(() => {
      this._renderCards();
    });
  }
}

if (!window.customElements.get('find-a-store')) {
  window.customElements.define('find-a-store', FindAStore);
}
